<template>
    <slide-out-panel
        :is-open="isOpen"
        :title="draft.id ? 'Edit Note' : 'New Note'"
        icon="fad fa-memo-pad"
        :width="760"
        @close="close"
    >
        <template v-if="isOpen">
            <b-field label="Title">
                <b-input v-model="draft.title" maxlength="255" placeholder="Note title" :has-counter="false"/>
            </b-field>

            <note-editor v-model="draft.content"/>
        </template>

        <template #footer>
            <b-button v-if="draft.id" type="is-danger" outlined icon-left="trash" class="mr-auto" @click="confirmDelete">Delete</b-button>
            <span class="save-status">
                <span v-if="saving"><i class="fas fa-spinner fa-spin mr-2"></i>Saving...</span>
                <span v-else-if="saveError" class="has-text-danger"><i class="fas fa-exclamation-triangle mr-2"></i>Could not save. Retrying on next change.</span>
                <span v-else-if="lastSavedAt" class="has-text-grey"><i class="fas fa-cloud-check mr-2"></i>Saved {{ formatTime(lastSavedAt) }}</span>
            </span>
            <b-button type="is-success" icon-left="save" @click="saveAndClose">Save</b-button>
        </template>
    </slide-out-panel>
</template>

<script>
// Shared note editor drawer with idle-debounced autosave. Pass `note` to edit
// an existing note or null for a new one. Emits `saved` with the server's note
// row on every successful save, `deleted` when the note is removed, and
// `close` when the drawer should be dismissed (pending edits are flushed).
import {client as http} from "@/http_client";
import debounce from "lodash/debounce";
import moment from "moment";
import NoteEditor from "@/components/NoteEditor";
import SlideOutPanel from "@/components/SlideOutPanel";
import {sanitizeHtml} from "@/utils/sanitizeHtml";

export default {
    name: 'NoteEditorPanel',
    components: {NoteEditor, SlideOutPanel},
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        enrollmentId: {
            type: [Number, String],
            required: true
        },
        note: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            draft: {id: null, title: '', content: ''},
            // Autosave state (Setup.vue shape, but idle-debounced: typing
            // continuously shouldn't hammer the API every couple seconds)
            autosaveReady: false,
            dirty: false,
            saving: false,
            lastSavedAt: null,
            saveError: false
        };
    },
    methods: {
        openDraft() {
            this.autosaveReady = false;
            this.dirty = false;
            this.saveError = false;
            this.lastSavedAt = null;
            this.draft = this.note
                ? {id: this.note.id, title: this.note.title, content: sanitizeHtml(this.note.content)}
                : {id: null, title: '', content: ''};
            this.$nextTick(() => {
                this.autosaveReady = true;
            });
        },
        // Drawer close (overlay, X, or after save): flush any pending edit
        close() {
            this.debouncedSave.cancel();
            if (this.dirty) this.saveNote(false);
            this.$emit('close');
        },
        saveAndClose() {
            this.debouncedSave.cancel();
            this.saveNote(true).then(saved => {
                if (saved) this.$emit('close');
            });
        },
        // Resolves true when the note is on the server, false otherwise.
        saveNote(explicit) {
            if (this.saving) return Promise.resolve(false);
            const title = this.draft.title.trim();
            if (!title) {
                // Autosave just waits for a title; an explicit save complains.
                if (explicit) this.$buefy.toast.open({message: 'Please give your note a title.', type: 'is-warning'});
                return Promise.resolve(false);
            }
            this.saving = true;
            // Changes made while the request is in flight re-set dirty and
            // re-arm the autosave timer, so nothing is lost.
            this.dirty = false;
            const payload = {title, content: this.draft.content};
            const request = this.draft.id
                ? http.put(`app/enrollments/${this.enrollmentId}/notes/${this.draft.id}`, payload, {force: true})
                : http.post(`app/enrollments/${this.enrollmentId}/notes`, payload, {force: true});
            return request
                .then(response => {
                    const saved = response.data.note;
                    if (!this.draft.id) {
                        // Writing the new id back into the draft shouldn't
                        // count as an edit — pause the watcher around it.
                        this.autosaveReady = false;
                        this.draft.id = saved.id;
                        this.$nextTick(() => {
                            this.autosaveReady = true;
                        });
                    }
                    this.saveError = false;
                    this.lastSavedAt = new Date();
                    this.$emit('saved', saved);
                    return true;
                })
                .catch(() => {
                    this.dirty = true;
                    this.saveError = true;
                    if (explicit) this.$buefy.toast.open({message: 'Could not save your note. Please try again.', type: 'is-danger'});
                    return false;
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        confirmDelete() {
            this.$buefy.dialog.confirm({
                title: 'Delete Note',
                message: `Are you sure you want to delete <b>${this.escapeHtml(this.draft.title)}</b>? This cannot be undone.`,
                confirmText: 'Delete',
                type: 'is-danger',
                onConfirm: () => this.deleteNote()
            });
        },
        deleteNote() {
            if (!this.draft.id) {
                // Never-saved draft: just close the editor.
                this.dirty = false;
                this.$emit('close');
                return;
            }
            http.delete(`app/enrollments/${this.enrollmentId}/notes/${this.draft.id}`, {force: true})
                .then(() => {
                    this.dirty = false;
                    this.$buefy.toast.open({message: 'Note deleted.', type: 'is-success'});
                    this.$emit('deleted', this.draft.id);
                    this.$emit('close');
                })
                .catch(() => {
                    this.$buefy.toast.open({message: 'Could not delete the note. Please try again.', type: 'is-danger'});
                });
        },
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        },
        formatTime(date) {
            return moment(date).format('h:mm:ss a');
        }
    },
    watch: {
        isOpen(open) {
            if (open) this.openDraft();
        },
        draft: {
            handler() {
                if (!this.autosaveReady || !this.isOpen) return;
                this.dirty = true;
                this.debouncedSave();
            },
            deep: true
        }
    },
    created() {
        // Save 5s after typing pauses; a long unbroken writing session
        // still checkpoints every 30s. Non-reactive on purpose.
        this.debouncedSave = debounce(() => this.saveNote(false), 5000, {maxWait: 30000});
    },
    beforeDestroy() {
        this.debouncedSave.cancel();
        if (this.isOpen && this.dirty) this.saveNote(false);
    }
};
</script>

<style scoped>
    .save-status {
        font-size: 0.85rem;
    }
    /* Push the Delete button to the drawer footer's left edge (fallback if the
       Bulma build lacks the auto spacing helper) */
    .mr-auto {
        margin-right: auto;
    }
</style>
